import { useQueryClient } from '@tanstack/react-query';
import { Button, ButtonVariants, rem } from '@triumph/design-system';
import { Tag } from 'primereact/tag';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useCurrentUserEmail } from '@/components/hooks/useCurrentUserEmail';
import { useApplicantContext } from '@/components/providers/ApplicantContextProvider';
import { FileUploadDialog } from '@/components/shared/components/FileUploadDialog';
import { TabDataState } from '@/components/shared/components/TabDataState';
import { getTabBadgeStyle } from '@/components/shared/constants/conditionsBadge';
import { useFeatureFlags } from '@/components/shared/constants/featureFlags';

import { TabBadgeSpinner } from '../../Applicant.StyledComponents';
import { ApplicantTabId, FileType } from '../../Applicant.types';

import { AddConditionDialog } from './AddConditionDialog';
import {
    Container,
    SECTION_HEADER_HEIGHT_VAR,
    SectionActions,
    OpenSectionToggle,
    SectionChevron,
    SectionDivider,
    SectionHeader,
    SectionTitle,
    SectionWrapper,
    Subsection,
} from './ApplicantConditions.StyledComponents';
import {
    Condition,
    ConditionResolutionStatus,
    ResolveConditionOptions,
} from './ApplicantConditions.types';
import { groupByCreator } from './ApplicantConditions.utils';
import { AttachedDocumentsDialog, useUploadConditionFile } from './AttachedDocumentsDialog';
import { BulkAddNoteModal } from './BulkAddNoteModal';
import { CollapsibleSectionHeader } from './CollapsibleSectionHeader';
import { ConditionFollowUpDialog } from './ConditionFollowUpDialog';
import { ConditionContent, ConditionHeader } from './ConditionRow';
import { useConditionSuccessBanner } from './ConditionSuccessBanner';
import { ConditionsAccordion } from './ConditionsAccordion';
import { DeleteConditionDialog } from './DeleteConditionDialog';
import { ResolveConditionDialog } from './ResolveConditionDialog';
import { useConditionAttachDialog } from './useConditionAttachDialog';
import { useConditionFocus } from './useConditionFocus';
import { usePublishedHeight } from './usePublishedHeight';

interface Props {
    openConditions: Condition[];
    closedConditions: Condition[];
    deletedConditions?: Condition[];
    focusConditionId?: string | null;
    focusNoteId?: string | null;
    onConditionFocused?: () => void;
    onNavigateToDdd?: () => void;
    isLoading?: boolean;
    isLoadingError?: boolean;
    onRetry?: () => void;
}

export const ApplicantConditions = ({
    openConditions,
    closedConditions,
    deletedConditions = [],
    focusConditionId = null,
    focusNoteId = null,
    onConditionFocused,
    isLoading = false,
    isLoadingError = false,
    onRetry,
    onNavigateToDdd,
}: Props) => {
    const { applicantId, applicantProductNames } = useApplicantContext();
    const id = applicantId ?? '';
    const queryClient = useQueryClient();

    const isCountKnown = !isLoading && !isLoadingError;

    const { isConditionResolutionStatusEnabled, isConditionCreatorSectionsEnabled } =
        useFeatureFlags();
    const currentUserEmail = useCurrentUserEmail();
    const [showAddCondition, setShowAddCondition] = useState(false);
    const [showBulkNote, setShowBulkNote] = useState(false);
    const [resolveTarget, setResolveTarget] = useState<Condition | null>(null);
    const [resolveOptions, setResolveOptions] = useState<ResolveConditionOptions>({});
    const [spawnParent, setSpawnParent] = useState<Condition | null>(null);
    const [followUpParentId, setFollowUpParentId] = useState<string | null>(null);
    const [loopParent, setLoopParent] = useState<Condition | null>(null);
    // The conditions list can lag the create, so remember parents that got a follow-up here.
    const [parentsWithAddedFollowUp, setParentsWithAddedFollowUp] = useState<ReadonlySet<string>>(
        () => new Set(),
    );
    const [deleteTarget, setDeleteTarget] = useState<Condition | null>(null);
    const [editTarget, setEditTarget] = useState<Condition | null>(null);
    const [uploadTarget, setUploadTarget] = useState<Condition | null>(null);
    const [droppedFiles, setDroppedFiles] = useState<File[]>([]);

    const openHeaderRef = useRef<HTMLDivElement>(null);
    // Measured, not hardcoded: the header's height follows the design-system button size.
    usePublishedHeight(openHeaderRef, SECTION_HEADER_HEIGHT_VAR, true);

    const uploadConditionFile = useUploadConditionFile();

    const [, setSearchParams] = useSearchParams();
    const { banner, showBanner } = useConditionSuccessBanner();
    const conditionsQueryKey = ['applicant-conditions', id];
    const knownConditionIdsRef = useRef<Set<string>>(new Set());

    const viewConditionInTab = (conditionId: string | null) =>
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('tab', ApplicantTabId.Conditions);
            if (conditionId) next.set('conditionId', conditionId);
            else next.delete('conditionId');
            next.delete('noteId');
            return next;
        });

    // The create endpoint returns no id, so the new row is the one absent from the pre-open snapshot.
    const findCreatedConditionId = (
        description: string,
        parentId: string | null,
        knownIds: Set<string>,
    ) => {
        const created = (queryClient.getQueryData<Condition[]>(conditionsQueryKey) ?? []).filter(
            (c) => !knownIds.has(c.id) && (!parentId || c.parentConditionId === parentId),
        );
        return (created.find((c) => c.description === description) ?? created[0])?.id ?? null;
    };

    const viewCreatedCondition = async (
        description: string,
        parentId: string | null,
        knownIds: Set<string>,
    ) => {
        let conditionId = findCreatedConditionId(description, parentId, knownIds);
        if (!conditionId) {
            await queryClient.refetchQueries({ queryKey: conditionsQueryKey });
            conditionId = findCreatedConditionId(description, parentId, knownIds);
        }
        viewConditionInTab(conditionId);
    };

    const handleConditionCreated = (description: string) => {
        const parentId = followUpParentId;
        const knownIds = knownConditionIdsRef.current;
        showBanner({
            kind: 'created',
            description,
            onViewNow: () => viewCreatedCondition(description, parentId, knownIds),
        });
    };

    const handleConditionResolved = (condition: Condition) => {
        showBanner({
            kind: 'resolved',
            description: condition.description,
            onViewNow: () => viewConditionInTab(condition.id),
        });
    };

    const openResolveDialog = (condition: Condition, options: ResolveConditionOptions = {}) => {
        setResolveOptions(options);
        setResolveTarget(condition);
    };
    const closeResolveDialog = () => {
        setResolveTarget(null);
        setResolveOptions({});
    };
    const handleResolveConflict = () => {
        queryClient.invalidateQueries({ queryKey: ['applicant-conditions', id] });
    };
    const handleSpawnConfirm = () => {
        setLoopParent(spawnParent);
        setFollowUpParentId(spawnParent?.id ?? null);
        setSpawnParent(null);
        setShowAddCondition(true);
    };
    const openAddFollowUp = (parentId: string) => {
        setFollowUpParentId(parentId);
        setShowAddCondition(true);
    };
    const closeAddCondition = () => {
        setShowAddCondition(false);
        setFollowUpParentId(null);
        setLoopParent(null);
    };
    const handleFollowUpAdded = (description: string) => {
        handleConditionCreated(description);
        if (followUpParentId) {
            const parentId = followUpParentId;
            setParentsWithAddedFollowUp((prev) => new Set(prev).add(parentId));
        }
        if (loopParent) {
            setShowAddCondition(false);
            setFollowUpParentId(null);
            setSpawnParent(loopParent);
        } else {
            closeAddCondition();
        }
    };
    const spawnParentHasFollowUp = () => {
        if (!spawnParent) return false;
        if (parentsWithAddedFollowUp.has(spawnParent.id)) return true;
        const all = [...openConditions, ...closedConditions, ...deletedConditions];
        return all.some(
            (c) =>
                c.parentConditionId === spawnParent.id ||
                (c.id === spawnParent.id && (c.childConditionIds?.length ?? 0) > 0),
        );
    };
    const skipFollowUp = () => {
        setSpawnParent(null);
        setLoopParent(null);
    };
    const openDeleteDialog = (condition: Condition) => setDeleteTarget(condition);
    const closeDeleteDialog = () => setDeleteTarget(null);
    const openEditDialog = (condition: Condition) => setEditTarget(condition);
    const closeEditDialog = () => setEditTarget(null);
    const openUploadDialog = (condition: Condition) => {
        prepareFileDialogOpen();
        setUploadTarget(condition);
    };
    const closeUploadDialog = () => {
        setUploadTarget(null);
        setDroppedFiles([]);
    };

    const handleConditionFileDrop = (condition: Condition, files: File[]) => {
        prepareFileDialogOpen();
        setDroppedFiles(files);
        setUploadTarget(condition);
    };

    const findConditionById = (conditionId: string) =>
        openConditions.find((c) => c.id === conditionId) ??
        closedConditions.find((c) => c.id === conditionId) ??
        deletedConditions.find((c) => c.id === conditionId) ??
        null;

    const findCurrent = (target: Condition | null) =>
        target ? (findConditionById(target.id) ?? target) : null;

    const handleUploadFile = async (file: File, fileType: FileType | null) => {
        if (!uploadTarget) return;
        await uploadConditionFile(id, uploadTarget.id, file, fileType);
    };

    useEffect(() => {
        if (!showAddCondition) return;
        knownConditionIdsRef.current = new Set(
            [...openConditions, ...closedConditions, ...deletedConditions].map((c) => c.id),
        );
    }, [showAddCondition]);

    const openGroups = isConditionCreatorSectionsEnabled
        ? groupByCreator(openConditions, currentUserEmail).filter((g) => g.conditions.length > 0)
        : [];

    const {
        containerRef,
        highlightedId,
        highlightedNoteId,
        expandedConditionId,
        isOpenSectionOpen,
        isClosedOpen,
        isDeletedOpen,
        isGroupOpen,
        setIsOpenExpanded,
        setIsClosedExpanded,
        setIsDeletedExpanded,
        setGroupCollapsed,
        setFocusIntent,
    } = useConditionFocus({
        applicantId: id,
        openConditions,
        closedConditions,
        deletedConditions,
        openGroups,
        findConditionById,
        focusConditionId,
        focusNoteId,
        onConditionFocused,
    });

    const {
        attachTarget,
        openAttachDialog,
        closeAttachDialog,
        prepareFileDialogOpen,
        attachDialogProps,
        uploadDialogProps,
    } = useConditionAttachDialog({ showBanner, setFocusIntent, uploadTarget });
    const currentAttachTarget = findCurrent(attachTarget);

    const canAddFollowUp = (condition: Condition) =>
        isConditionResolutionStatusEnabled &&
        (condition.resolutionStatus === ConditionResolutionStatus.Problematic ||
            condition.resolutionStatus === ConditionResolutionStatus.Approved);

    const renderHeader = (
        condition: Condition,
        onDocsClick?: () => void,
        withCheckbox = false,
        onOpen?: () => void,
        isOpen?: boolean,
        onDelete?: () => void,
        onAddFollowUp?: () => void,
        onEdit?: () => void,
    ) => (
        <ConditionHeader
            applicantId={id}
            condition={condition}
            onCheckboxClick={withCheckbox ? () => openResolveDialog(condition) : undefined}
            onDocumentsClick={onDocsClick}
            onDelete={onDelete}
            onEdit={onEdit}
            onAddFollowUp={onAddFollowUp}
            onOpen={onOpen}
            isOpen={isOpen}
            onNavigateToCondition={(conditionId) => setFocusIntent({ conditionId, target: 'row' })}
            onNavigateToDdd={onNavigateToDdd}
        />
    );

    const renderOpenAccordion = (conditions: Condition[]) => (
        <ConditionsAccordion
            conditions={conditions}
            highlightConditionId={highlightedId}
            expandedConditionId={expandedConditionId}
            onFileDrop={handleConditionFileDrop}
            renderHeader={(condition, onOpen, isOpen) =>
                renderHeader(
                    condition,
                    () => openAttachDialog(condition),
                    true,
                    onOpen,
                    isOpen,
                    () => openDeleteDialog(condition),
                    undefined,
                    () => openEditDialog(condition),
                )
            }
            renderContent={(condition) => (
                <ConditionContent
                    applicantId={id}
                    condition={condition}
                    onDelete={() => openDeleteDialog(condition)}
                    onResolve={(options) => openResolveDialog(condition, options)}
                    onAttachDocument={() => openUploadDialog(condition)}
                    highlightNoteId={highlightedNoteId}
                />
            )}
        />
    );

    return (
        <Container ref={containerRef} data-testid="applicant-conditions">
            <SectionWrapper>
                <SectionHeader ref={openHeaderRef} $collapsed={!isOpenSectionOpen}>
                    {/* Only the title toggles; the action buttons can't nest inside a button. */}
                    <OpenSectionToggle
                        type="button"
                        onClick={() => setIsOpenExpanded(!isOpenSectionOpen)}
                        aria-expanded={isOpenSectionOpen}
                        data-testid="conditions-open-toggle"
                    >
                        <SectionTitle as="span">
                            <SectionChevron aria-hidden>
                                {isOpenSectionOpen ? '▾' : '▸'}
                            </SectionChevron>
                            Open Tasks/Conditions
                            {isLoading ? <TabBadgeSpinner strokeWidth="6" /> : null}
                            {isCountKnown ? (
                                <Tag
                                    severity="info"
                                    value={openConditions.length}
                                    rounded
                                    style={{
                                        fontSize: rem(11),
                                        padding: `${rem(1)} ${rem(6)}`,
                                        ...getTabBadgeStyle(openConditions.length),
                                    }}
                                />
                            ) : null}
                        </SectionTitle>
                    </OpenSectionToggle>
                    <SectionDivider />
                    <SectionActions>
                        <Button
                            variant={ButtonVariants.secondary}
                            onClick={() => setShowBulkNote(true)}
                            disabled={openConditions.length === 0}
                            data-testid="conditions-bulk-add-note"
                        >
                            + Bulk Add Note
                        </Button>
                        <Button
                            variant={ButtonVariants.primary}
                            onClick={() => setShowAddCondition(true)}
                            data-testid="conditions-add-condition"
                        >
                            + Add Task/Condition
                        </Button>
                    </SectionActions>
                </SectionHeader>
                {/* As in Closed, loading and error stay visible while collapsed. */}
                {isOpenSectionOpen || isLoading || isLoadingError ? (
                    <TabDataState
                        isLoading={isLoading}
                        isLoadingError={isLoadingError}
                        onRetry={onRetry}
                        isEmpty={openConditions.length === 0}
                        emptyMessage="No open tasks/conditions."
                        errorMessage="Could not load open tasks/conditions."
                    >
                        {isConditionCreatorSectionsEnabled
                            ? openGroups.map((group) => {
                                  const isOpen = isGroupOpen(group.key, group.conditions);
                                  return (
                                      <Subsection
                                          key={group.key}
                                          data-testid={`conditions-open-${group.key}`}
                                      >
                                          <CollapsibleSectionHeader
                                              title={group.title}
                                              isOpen={isOpen}
                                              onToggle={() => setGroupCollapsed(group.key, isOpen)}
                                              testId={`conditions-open-${group.key}-toggle`}
                                              isSubsection
                                          >
                                              <Tag
                                                  severity="info"
                                                  value={group.conditions.length}
                                                  rounded
                                                  style={{
                                                      fontSize: rem(11),
                                                      padding: `${rem(1)} ${rem(6)}`,
                                                  }}
                                              />
                                          </CollapsibleSectionHeader>
                                          {isOpen ? renderOpenAccordion(group.conditions) : null}
                                      </Subsection>
                                  );
                              })
                            : renderOpenAccordion(openConditions)}
                    </TabDataState>
                ) : null}
            </SectionWrapper>

            <SectionWrapper>
                <CollapsibleSectionHeader
                    title="Closed Tasks/Conditions"
                    isOpen={isClosedOpen}
                    onToggle={() => setIsClosedExpanded(!isClosedOpen)}
                    testId="conditions-closed-toggle"
                >
                    {isLoading ? <TabBadgeSpinner strokeWidth="6" /> : null}
                    {isCountKnown ? (
                        <Tag
                            severity="info"
                            value={closedConditions.length}
                            rounded
                            style={{
                                fontSize: rem(11),
                                padding: `${rem(1)} ${rem(6)}`,
                            }}
                        />
                    ) : null}
                </CollapsibleSectionHeader>
                {/* Loading and error stay visible while collapsed so a failed fetch isn't hidden behind the toggle. */}
                {isClosedOpen || isLoading || isLoadingError ? (
                    <TabDataState
                        isLoading={isLoading}
                        isLoadingError={isLoadingError}
                        onRetry={onRetry}
                        isEmpty={closedConditions.length === 0}
                        emptyMessage="No closed tasks/conditions."
                        errorMessage="Could not load closed tasks/conditions."
                    >
                        <ConditionsAccordion
                            conditions={closedConditions}
                            highlightConditionId={highlightedId}
                            expandedConditionId={expandedConditionId}
                            renderHeader={(condition, onOpen, isOpen) =>
                                renderHeader(
                                    condition,
                                    condition.files.length > 0
                                        ? () => openAttachDialog(condition)
                                        : undefined,
                                    false,
                                    onOpen,
                                    isOpen,
                                    undefined,
                                    canAddFollowUp(condition)
                                        ? () => openAddFollowUp(condition.id)
                                        : undefined,
                                )
                            }
                            renderContent={(condition) => (
                                <ConditionContent
                                    applicantId={id}
                                    condition={condition}
                                    onAddFollowUp={
                                        canAddFollowUp(condition)
                                            ? () => openAddFollowUp(condition.id)
                                            : undefined
                                    }
                                    highlightNoteId={highlightedNoteId}
                                />
                            )}
                        />
                    </TabDataState>
                ) : null}
            </SectionWrapper>

            {deletedConditions.length > 0 ? (
                <SectionWrapper>
                    <CollapsibleSectionHeader
                        title="Deleted Tasks/Conditions"
                        isOpen={isDeletedOpen}
                        onToggle={() => setIsDeletedExpanded(!isDeletedOpen)}
                        testId="conditions-deleted-toggle"
                    >
                        <Tag
                            severity="danger"
                            value={deletedConditions.length}
                            rounded
                            style={{
                                fontSize: rem(11),
                                padding: `${rem(1)} ${rem(6)}`,
                            }}
                        />
                    </CollapsibleSectionHeader>
                    {isDeletedOpen ? (
                        <ConditionsAccordion
                            conditions={deletedConditions}
                            highlightConditionId={highlightedId}
                            expandedConditionId={expandedConditionId}
                            isDeleted
                            renderHeader={(condition, onOpen, isOpen) =>
                                renderHeader(
                                    condition,
                                    condition.files.length > 0
                                        ? () => openAttachDialog(condition)
                                        : undefined,
                                    false,
                                    onOpen,
                                    isOpen,
                                )
                            }
                            renderContent={(condition) => (
                                <ConditionContent
                                    applicantId={id}
                                    condition={condition}
                                    highlightNoteId={highlightedNoteId}
                                />
                            )}
                        />
                    ) : null}
                </SectionWrapper>
            ) : null}

            <AddConditionDialog
                applicantId={id}
                availableProducts={applicantProductNames}
                visible={showAddCondition}
                onClose={closeAddCondition}
                onAdded={handleFollowUpAdded}
                parentConditionId={followUpParentId ?? undefined}
            />
            <BulkAddNoteModal
                applicantId={id}
                openConditions={openConditions}
                visible={showBulkNote}
                onClose={() => setShowBulkNote(false)}
            />
            <ResolveConditionDialog
                applicantId={id}
                condition={resolveTarget}
                visible={Boolean(resolveTarget)}
                onClose={closeResolveDialog}
                onConflict={handleResolveConflict}
                initialNote={resolveOptions.initialNote}
                resolutionMethod={resolveOptions.resolutionMethod}
                onProblematic={setSpawnParent}
                onResolved={handleConditionResolved}
            />
            <ConditionFollowUpDialog
                condition={spawnParent}
                visible={Boolean(spawnParent)}
                isAnother={spawnParentHasFollowUp()}
                onSkip={skipFollowUp}
                onConfirm={handleSpawnConfirm}
            />
            <DeleteConditionDialog
                applicantId={id}
                condition={deleteTarget}
                visible={Boolean(deleteTarget)}
                onClose={closeDeleteDialog}
            />
            <AddConditionDialog
                applicantId={id}
                availableProducts={applicantProductNames}
                visible={Boolean(editTarget)}
                onClose={closeEditDialog}
                onAdded={closeEditDialog}
                editCondition={editTarget}
            />
            <AttachedDocumentsDialog
                applicantId={id}
                condition={currentAttachTarget}
                visible={Boolean(attachTarget)}
                onClose={closeAttachDialog}
                {...attachDialogProps}
                canAttach={!currentAttachTarget?.resolved && !currentAttachTarget?.isDeleted}
            />
            <FileUploadDialog
                id="condition-upload-modal"
                header="Attach Documents to Task/Condition"
                visible={Boolean(uploadTarget)}
                onClose={closeUploadDialog}
                onUploadFile={handleUploadFile}
                {...uploadDialogProps}
                contextLabel={uploadTarget?.description ?? ''}
                initialFiles={droppedFiles}
                data-testid="condition-upload-dialog"
            />
            {banner}
        </Container>
    );
};
